import { sessionIdentity } from '../auth-session';
import { useCallback, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { getTokens } from '../auth-storage';
import { useRemote } from '../operations/use-remote';
import { getAccess } from './access.repository';
import { AccessContext, useAccess } from './access.context';
import { Alert } from '../../components/ui/alert';
function subscribe(listener: () => void) {
  window.addEventListener('nissei-session-change', listener);
  return () => window.removeEventListener('nissei-session-change', listener);
}
function AccessMessages() {
  const access = useAccess();
  return (
    <>
      {access.error && (
        <Alert {...(access.refresh ? { onRetry: access.refresh } : {})}>
          Your access could not be checked. Actions are unavailable until access is verified.{' '}
          {access.error}
        </Alert>
      )}
      {access.data?.employee.status === 'inactive' && (
        <Alert>
          Your login is linked to an inactive employee. Ask an administrator to reactivate the
          employee or link the correct active employee before approving or changing procurement
          records.
        </Alert>
      )}
      {access.data?.employee.status === 'not_linked' && (
        <Alert>
          Your login is not linked to an active employee. Ask an administrator to link your login
          under Business records → People → Employees → Login account before approving or changing
          procurement records.
        </Alert>
      )}
    </>
  );
}
function ProcurementAccess({ children }: { children: ReactNode }) {
  const { refresh } = useAccess();
  useEffect(() => {
    refresh?.();
  }, [refresh]);
  return (
    <>
      <AccessMessages />
      {children}
    </>
  );
}
export function AccessProvider({
  children,
  showMessages = true,
}: {
  children: ReactNode;
  showMessages?: boolean;
}) {
  const parent = useAccess();
  return parent.refresh ? (
    <ProcurementAccess>{children}</ProcurementAccess>
  ) : (
    <SessionAccess showMessages={showMessages}>{children}</SessionAccess>
  );
}
function SessionAccess({ children, showMessages }: { children: ReactNode; showMessages: boolean }) {
  const token = useSyncExternalStore(subscribe, () => sessionIdentity(getTokens()?.accessToken ?? ''));
  const load = useCallback(
    (signal: AbortSignal) => {
      void token;
      return getAccess(signal);
    },
    [token]
  );
  const access = useRemote(load, Boolean(token), true);
  useEffect(() => {
    window.addEventListener('nissei-access-refresh', access.retry);
    window.addEventListener('focus', access.retry);
    return () => {
      window.removeEventListener('nissei-access-refresh', access.retry);
      window.removeEventListener('focus', access.retry);
    };
  }, [access.retry]);
  const data = token && !access.error ? access.data : null;
  return (
    <AccessContext.Provider
      value={{
        data,
        error: access.error,
        refresh: access.retry,
        allows: (permission) => Boolean(data?.permissions.includes(permission)),
      }}
    >
      {showMessages && <AccessMessages />}
      {children}
    </AccessContext.Provider>
  );
}
